import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Gallery from './components/Gallery';
import Description from './components/Description';
import Footer from './components/Footer';
import './styles/App.css';

interface Photo {
  id: number;
  filename: string;
  original_name: string;
  sort_order: number;
  uploaded_at: string;
}

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3000/api';

function getSessionId(): string {
  let sid = sessionStorage.getItem('_sid');
  if (!sid) {
    sid = Math.random().toString(36).substring(2, 11) + Date.now().toString(36);
    sessionStorage.setItem('_sid', sid);
  }
  return sid;
}

function getUTMParams(): { utm_source?: string; utm_medium?: string; utm_campaign?: string } {
  const params = new URLSearchParams(window.location.search);
  const result: { utm_source?: string; utm_medium?: string; utm_campaign?: string } = {};
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  const campaign = params.get('utm_campaign');
  if (source) result.utm_source = source;
  if (medium) result.utm_medium = medium;
  if (campaign) result.utm_campaign = campaign;
  return result;
}

function App() {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [description, setDescription] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track visitor on page load
  useEffect(() => {
    const trackVisitor = async () => {
      try {
        const utmParams = getUTMParams();
        await fetch(`${API_BASE}/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pagePath: window.location.pathname,
            referrer: document.referrer,
            ...utmParams
          })
        });
      } catch {
        // Silently fail
      }
    };
    trackVisitor();
  }, []);

  // Track session duration on page leave
  useEffect(() => {
    const sessionId = getSessionId();
    const startTime = Date.now();

    const handlePageHide = () => {
      const duration = Math.round((Date.now() - startTime) / 1000);
      try {
        const blob = new Blob(
          [JSON.stringify({ sessionId, eventType: 'session_end', eventData: { duration } })],
          { type: 'application/json' }
        );
        navigator.sendBeacon(`${API_BASE}/analytics/event`, blob);
      } catch {
        // Silently fail
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, []);

  // Track scroll depth milestones
  useEffect(() => {
    const sessionId = getSessionId();
    const reached = new Set<number>();

    const handleScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const scrollPercent = Math.round((window.scrollY / docHeight) * 100);

      for (const milestone of [25, 50, 75, 100]) {
        if (scrollPercent >= milestone && !reached.has(milestone)) {
          reached.add(milestone);
          fetch(`${API_BASE}/analytics/event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, eventType: 'scroll_depth', eventData: { depth: milestone } })
          }).catch(() => {});
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [photosRes, descRes] = await Promise.all([
          fetch(`${API_BASE}/photos`),
          fetch(`${API_BASE}/description`)
        ]);

        if (!photosRes.ok || !descRes.ok) throw new Error('Failed to fetch data');

        const photosData = await photosRes.json();
        const descData = await descRes.json();
        setPhotos(photosData);
        setDescription(descData.description);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Unable to load property data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading property details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Something went wrong</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="app">
      <Header />
      <main>
        <Gallery photos={photos} apiBase={API_BASE} />
        <Description content={description} />
      </main>
      <Footer />
    </div>
  );
}

export default App;
